<template>
  <div v-if="showActivity" class="fixed bottom-20 left-4 z-40">
    <div :class="`border rounded-lg shadow-lg p-4 ${expandedView ? 'max-w-lg w-96' : 'max-w-sm'} transition-all duration-300 ${applyThemeClasses('layout.card')}`">
      <div class="flex items-center justify-between mb-2">
        <h4 :class="`font-semibold text-sm ${applyThemeClasses('text.primary')}`">🏆 Performance Records</h4>
        <button 
          @click="showActivity = false"
          :class="`hover:opacity-70 text-xs ${applyThemeClasses('text.secondary')}`"
        >
          ×
        </button>
      </div>

      <!-- Summary -->
      <div v-if="notificationSummary.total > 0" class="space-y-3">
        <div :class="`text-sm ${applyThemeClasses('text.secondary')}`">
          <div class="flex justify-between items-center mb-2">
            <span>New Records:</span>
            <span class="font-semibold text-green-600">{{ notificationSummary.newRecords }}</span>
          </div>
          <div v-if="notificationSummary.recordsBeaten > 0" class="flex justify-between items-center">
            <span>Records Beaten:</span>
            <span class="font-semibold text-red-600">{{ notificationSummary.recordsBeaten }}</span>
          </div>
        </div>

        <!-- Toggle Expanded View -->
        <div class="flex justify-between items-center mb-2">
          <button
            @click="expandedView = !expandedView"
            :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.secondary')}`"
          >
            {{ expandedView ? 'Compact View' : 'Detailed View' }}
          </button>
          <button
            @click="markAllAsRead"
            :class="`text-xs px-2 py-1 rounded ${applyThemeClasses('button.secondary')}`"
          >
            Mark All Read
          </button>
        </div>

        <!-- Notifications List -->
        <div class="border-t pt-2">
          <div :class="`space-y-1 overflow-y-auto ${expandedView ? 'max-h-64' : 'max-h-32'}`">
            <div 
              v-for="notification in displayedNotifications" 
              :key="notification.id"
              :class="`text-xs p-2 rounded border cursor-pointer hover:opacity-80 ${
                notification.type === 'new_record' 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-red-300 bg-red-50'
              }`"
              @click="viewNotification(notification)"
            >
              <div class="flex items-center gap-1">
                <span>{{ getNotificationIcon(notification) }}</span>
                <span class="font-semibold">{{ getNotificationTitle(notification) }}</span>
              </div>
              
              <div class="mt-1">
                <div class="font-medium">{{ notification.chocoboName }}</div>
                <div class="flex justify-between items-center">
                  <span>{{ formatTime(notification.time) }}</span>
                  <span :class="applyThemeClasses('text.secondary')">
                    {{ formatTrackType(notification.raceType) }}
                  </span>
                </div>
                
                <!-- Time Comparison -->
                <div v-if="notification.type === 'new_record' && notification.previousRecord" class="text-green-600">
                  ↑ {{ formatTimeDifference(notification.previousRecord, notification.time) }} faster
                </div>
                <div v-else-if="notification.type === 'record_beaten'" class="text-red-600">
                  Beaten by {{ notification.beatenBy.name }} ({{ formatTime(notification.beatenBy.time) }})
                </div>
              </div>
              
              <!-- Days Ago -->
              <div class="text-xs mt-1" :class="applyThemeClasses('text.secondary')">
                {{ getDaysAgo(notification.day) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Notifications Message -->
      <div v-else :class="`text-sm text-center py-4 ${applyThemeClasses('text.secondary')}`">
        No recent performance records
      </div>
    </div>
  </div>

  <!-- Toggle Button -->
  <button 
    v-if="!showActivity"
    @click="showActivity = true"
    :class="`fixed bottom-20 left-4 z-40 ${
      unreadCount > 0 ? applyThemeClasses('button.primary') : applyThemeClasses('button.secondary')
    } rounded-full p-2 shadow-lg transition-colors`"
    title="Show Performance Records"
  >
    <span class="text-sm">🏆</span>
    <span 
      v-if="unreadCount > 0" 
      :class="`absolute -top-1 -right-1 ${
        notificationSummary.recordsBeaten > 0 ? 'bg-red-500' : 'bg-green-500'
      } text-white text-xs rounded-full w-5 h-5 flex items-center justify-center`"
    >
      {{ unreadCount }}
    </span>
  </button>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue'
import { useGameStore } from '@/store/game'
import { applyThemeClasses } from '@/utils/themeColors'

const emit = defineEmits(['view-chocobo'])
const gameStore = useGameStore()
const showActivity = ref(false)
const expandedView = ref(false)

const unreadNotifications = computed(() => {
  return (gameStore.performanceNotifications || []).filter(n => !n.read)
})

const unreadCount = computed(() => {
  return unreadNotifications.value.length
})

const displayedNotifications = computed(() => {
  const notifications = expandedView.value 
    ? unreadNotifications.value 
    : unreadNotifications.value.slice(0, 5)
  return notifications
})

const notificationSummary = computed(() => {
  const unread = unreadNotifications.value
  return {
    total: unread.length,
    newRecords: unread.filter(n => n.type === 'new_record').length,
    recordsBeaten: unread.filter(n => n.type === 'record_beaten').length
  }
})

const getNotificationIcon = (notification) => {
  if (notification.type === 'new_record') {
    if (notification.recordType === 'all_time') return '🥇'
    if (notification.recordType === 'monthly') return '🥈'
    return '⭐' // personal best
  }
  return '💔' // record beaten
}

const getNotificationTitle = (notification) => {
  if (notification.type === 'new_record') {
    if (notification.recordType === 'all_time') return 'New All-Time Record!'
    if (notification.recordType === 'monthly') return 'New Monthly Record!'
    return 'Personal Best!'
  }
  return 'Record Broken'
}

const formatTrackType = (raceType) => {
  const types = {
    short: '800m Sprint',
    medium: '2000m Classic',
    long: '5000m Endurance'
  }
  return types[raceType] || raceType
}

const formatTime = (time) => {
  if (!time || typeof time !== 'number') return 'N/A'
  const minutes = Math.floor(time / 60)
  const seconds = Math.floor(time % 60)
  const milliseconds = Math.floor((time % 1) * 1000)
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
}

const formatTimeDifference = (oldTime, newTime) => {
  const diff = oldTime - newTime
  return diff.toFixed(3) + 's'
}

const getDaysAgo = (day) => {
  const daysAgo = gameStore.currentDay - day
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  return `${daysAgo} days ago`
}

const viewNotification = (notification) => {
  notification.read = true
  emit('view-chocobo', notification.chocoboId)
  showActivity.value = false
}

const markAllAsRead = () => {
  gameStore.performanceNotifications.forEach(n => n.read = true)
}
</script>